<template>
  <div class="md:px-8">
    <div class="timeline-steps gap-4">
      <div
        v-for="(step, index) in steps"
        :key="index"
        class="timeline-step"
        :class="getStepClass(step, statusName)"
      >
        <!-- Step content with icon -->
        <div class="step-container">
          <!-- Circle icon -->
          <div class="step-icon-container">
            <div
              class="step-icon"
              :style="{
                backgroundColor: step.circleFill,
                borderColor: step.circleBorder,
                borderWidth: '2px'
              }"
            >
              <component :is="step.customIcon" />
            </div>
            <!-- Line connecting next step -->
            <div
              v-if="index < steps.length - 1"
              class="step-line"
              :style="{ backgroundColor: step.lineColor, width: '2px' }"
            ></div>
          </div>
          <!-- Step content -->
          <div class="step-content">
            <div
              v-if="step.editable"
              class="flex flex-col items-center md:flex-row gap-4 relative"
            >
              <StepDateTimeInput
                :step="step"
                :default-date="
                  appointment.arrival_window_start || appointment.scheduled_time
                "
                @update:modelValue="handleTimeChange"
              />
            </div>
            <div v-else class="grid grid-cols-2">
              <div class="flex flex-col">
                <div class="font-bold" :class="{ 'text-gray-400': step.grey }">
                  {{ step.title }}
                </div>
                <div class="flex gap-1 items-baseline">
                  <div class="text-xs" :class="{ 'text-gray-400': step.grey }">
                    {{ step.time }}
                  </div>
                  <div
                    class="text-xxs"
                    :class="{
                      'text-gray-500': !step.grey,
                      'text-gray-400': step.grey
                    }"
                  >
                    {{ step.date }}
                  </div>
                </div>
              </div>
              <div class="flex flex-col">
                <TimeBadge
                  v-if="step.active && expanded"
                  :label="timeBadge.label"
                  :time="timeBadge.time"
                  :text-only="timeBadge.textOnly || timeBadge.time === '--'"
                  :background-color="timeBadge.backgroundColor"
                >
                  <template #icon
                    ><component :is="timeBadge.icon"></component
                  ></template>
                </TimeBadge>
                <div v-else>
                  <div
                    class="font-bold"
                    :class="{ 'text-gray-400': step.grey }"
                  >
                    {{ step.durationLabel }}
                  </div>
                  <div class="text-xs" :class="{ 'text-gray-400': step.grey }">
                    {{ step.duration }}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref } from "vue";

import TimeBadge from "@/components/Seranova/TimeBadge/TimeBadge.vue";
import { useAppointmentTimes } from "@/AppointmentDetails/useAppointmentTimes";
import { AppointmentV2 } from "@/typings/appointment";
import StepDateTimeInput from "./StepDateTimeInput.vue";

const emit = defineEmits(["update:modelValue"]);
const props = defineProps<{
  appointment: AppointmentV2;
  editableTimes?: string[];
  lostTimes?: string[];
}>();
const expanded = ref(true);

const { steps, timeBadge, getStepClass, statusName } =
  useAppointmentTimes(props);

const handleTimeChange = (value: { [k: string]: string }) => {
  emit("update:modelValue", value);
};
</script>

<style scoped>
.timeline-steps {
  position: relative;
  display: flex;
  flex-direction: column;
}

.timeline-step {
  position: relative;
  padding: 8px;
  border-radius: 8px;
}

/* Existing blue dashed border for active step */
.editable-step {
  border: 2px dashed #f05252;
  background-color: #fdf2f2;
  margin: -2px;
}

/* Existing blue dashed border for active step */
.active-step {
  border: 2px dashed #1c64f2;
  margin: -2px;
}

/* New style for pending_assigned and completed departure steps */
.pending-assigned-step {
  background-color: #e5e7eb;
  border: none;
  margin: 0;
}

.lost-step {
  border: 2px dashed #f05252;
  background-color: #fdf2f2;
  margin: -2px;
}

.step-container {
  display: flex;
  align-items: flex-start;
  gap: 16px;
}

.step-icon-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  z-index: 9;
}

.step-icon {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  border: 1px solid;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2;
  background-color: white;
}

.step-line {
  position: absolute;
  top: 24px;
  width: 1px;
  height: calc(100% + 26px);
}

.step-content {
  flex: 1;
}
</style>
